import type { Token } from "markdown-it";

// The note view keeps the blank lines of the note as written, so what is on
// screen has the same spacing as the text Copy to chart puts on the
// clipboard (stripMarkdown keeps every line break and blank line). Each
// block that follows one or more blank lines gets `data-gap="N"`, styled as
// N empty lines (editor-markdown.css); a block right under the previous one
// (a heading over its list) gets none. Clinical lead, 30/10.

// Larger runs are shown as this many lines.
export const MAX_LINE_GAP = 4;

const isBlank = (line: string | undefined): boolean =>
  line === undefined || line.trim() === "";

// Marks each block token that follows blank lines in `source` (the text the
// tokens were parsed from). Horizontal rules are hidden (`data-copy-hidden`):
// the copy drops them, and a rule between two blank lines leaves one.
export const markLineGaps = (tokens: Token[], source: string): void => {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  // The last line with text of a block (its map can end on blank lines).
  const lastText = (map: [number, number]): number => {
    let end = map[1];
    while (end > map[0] && isBlank(lines[end - 1])) end -= 1;
    return end;
  };
  // Per nesting level: where the previous sibling's text ended, and a rule
  // skipped since then.
  const previousEnd: (number | undefined)[] = [];
  const rule: ({ before: number; end: number } | undefined)[] = [];
  for (const token of tokens) {
    if (!token.block || !token.map || token.nesting === -1) continue;
    if (token.type === "inline") continue;
    const level = token.level;
    const end = previousEnd[level];
    if (token.type === "hr") {
      token.attrSet("data-copy-hidden", "");
      rule[level] = {
        before: end === undefined ? 0 : Math.max(0, token.map[0] - end),
        end: token.map[1],
      };
      continue;
    }
    const skipped = rule[level];
    let gap = 0;
    if (skipped) {
      const after = Math.max(0, token.map[0] - skipped.end);
      gap =
        end === undefined
          ? 0
          : skipped.before + after - (skipped.before > 0 && after > 0 ? 1 : 0);
      rule[level] = undefined;
    } else if (end !== undefined) {
      gap = Math.max(0, token.map[0] - end);
    }
    if (gap > 0) token.attrSet("data-gap", String(Math.min(gap, MAX_LINE_GAP)));
    previousEnd[level] = lastText(token.map);
    // A new block starts its own children's run.
    previousEnd.length = level + 1;
    rule.length = level + 1;
  }
};

// Blank lines before each chunk of `text` (the preamble and the sections of
// SectionedNote, in order, each as found in the text): the first chunk gets
// 0. A chunk that cannot be found gets one blank line.
export const chunkGaps = (text: string, chunks: string[]): number[] => {
  const normalized = text.replace(/\r\n?/g, "\n");
  let cursor = 0;
  return chunks.map((chunk, index) => {
    const at = normalized.indexOf(chunk, cursor);
    if (at < 0) return index === 0 ? 0 : 1;
    const between = normalized.slice(cursor, at);
    cursor = at + chunk.length;
    if (index === 0) return 0;
    const breaks = between.split("\n").length - 1;
    return Math.min(Math.max(0, breaks - 1), MAX_LINE_GAP);
  });
};
